import React, { useContext, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button, Tabs, Tab } from 'react-bootstrap';
import { useReactToPrint } from 'react-to-print';
import { snapdom } from "@zumer/snapdom";
import GridCellRating from "../../../components/ui/GridCellRating";
import DataGrid from "../../../components/DataGrid";
import Loader from "../../../components/ui/Loader";
import { AppLoadContext } from "../../../components/ui/AppLoadContext";
import {
    getAdvisoryGroupRepresentativenessByType,
    resetRepresentativenessData,
} from '../../../ducks/advisoryGroup';
import { toDecimal, getScoreTypeWithDifference, calculateDifference, exportData } from "../../../utils/utils";
import {
    AG_REPSCORE_COLUMN_HEADER_PREFIX,
    AG_REPSCORE_TYPE,
    AGGREGATE_SCORES_OTHER,
    ALERT_TYPE,
    REPSCORE_CATEGORIES_DEMOGRAPHIC, REPSCORE_CATEGORIES_GEOGRAPHIC
} from '../../../constants';
import geoAnalysis from './../../../assets/images/ellipse-overlap.jpg';
import styles from './RepresentativenessDetailsModal.module.scss';
import messages from '../../../utils/helper/messages';
import { AlertContext } from "../../../context/AlertContext";

const RepresentativenessDetailsModal = ({ show, id, type, onCancel, agType }) => {
    const dispatch = useDispatch();
    const isAppLoaded = useContext(AppLoadContext);
    const { showAlert } = useContext(AlertContext);

    const contentRef = useRef(null);
    const repsDetailData = useSelector((state) => state.advisoryGroup.representativenessData);

    const [title, setTitle] = useState('');
    const [columns, setColumns] = useState([]);
    const [repsDetails, setRepsDetails] = useState([]);
    const [geographyDetails, setGeographyDetails] = useState([]);
    const [geographySummary, setGeographySummary] = useState(null);
    const [key, setKey] = useState('demographics');

    const demographyCategories = Object.values(REPSCORE_CATEGORIES_DEMOGRAPHIC);
    const geographyCategories = Object.values(REPSCORE_CATEGORIES_GEOGRAPHIC);

    const handlePrint = useReactToPrint({
        documentTitle: `${title}-${id}`,
        removeAfterPrint: true,
    });

    const hideColumn = {
        category: false,
    };

    const baseColumns = [
        {
            id: 'category',
            header: 'Category',
            accessorKey: 'category',
            enableSorting: false,
        },
        {
            id: 'categoryValue',
            header: 'Category',
            accessorKey: 'categoryValue',
            cell: ({ row }) => row.original.categoryValue,
            aggregatedCell: ({ row }) => (<div className="fw-bold">{row.original.category}</div>),
            enableSorting: false,
            size: 215,
        },
        {
            id: 'populationPercent',
            header: 'Baseline Result',
            accessorKey: 'populationPercent',
            cell: ({ row }) =>  row.original.populationPercent !== null ? toDecimal(row.original.populationPercent * 100) : '',
            aggregatedCell: () => null,
            enableSorting: false,
        },
    ];

    const scoreColumns = [
        {
            id: 'panelMembershipPercent',
            header: `${AG_REPSCORE_COLUMN_HEADER_PREFIX[type]} Result`,
            accessorKey: 'panelMembershipPercent',
            cell: ({ row }) => row.original.panelMembershipPercent !== null ? toDecimal(row.original.panelMembershipPercent * 100) : '',
            aggregatedCell: () => null,
            enableSorting: false,
        },
        {
            id: 'difference',
            header: 'Difference',
            accessorKey: 'difference',
            cell: ({ row }) => {
                const populationPercent = row.original.populationPercent;
                const panelMembershipPercent = row.original.panelMembershipPercent;
                if (populationPercent === null || panelMembershipPercent === null) return '';
                return calculateDifference(populationPercent, panelMembershipPercent);
            },
            enableSorting: false,
        },
        {
            id: 'panelRating',
            header: 'Score',
            accessorKey: 'panelRating',
            cell: ({ row }) => {
                const difference = calculateDifference(row.original.populationPercent, row.original.panelMembershipPercent);

                if (difference === null) return '';
                const rating = getScoreTypeWithDifference(difference);

                return <GridCellRating rating={rating} type={agType} customClass="my-1" />;
            },
            aggregatedCell: ({ row }) => row.original?.panelRating ? <GridCellRating rating={row.original.panelRating} type={agType} customClass="my-1" isAggregate={true} /> : null,
            enableSorting: false,
        },
    ];

    const geoAnalysisColumns = [
        {
            id: 'category',
            header: 'Category',
            accessorKey: 'category',
            enableSorting: false,
        },
        {
            id: 'categoryValue',
            header: 'Category',
            accessorKey: 'categoryValue',
            cell: ({ row }) => row.original.categoryValue,
            aggregatedCell: ({ row }) => (<div className="fw-bold">{row.original.category}</div>),
            enableSorting: false,
            size: 215,
        },
        {
            id: 'panelMembershipPercent',
            header: 'Result',
            accessorKey: 'panelMembershipPercent',
            cell: ({ row }) => {
                if (typeof row.original.panelMembershipPercent === "string" && row.original.panelMembershipPercent === "showGraphic") {
                    return (<div className={styles.geoAnalysis}>
                        <img src={geoAnalysis} alt="Geographic Analysis" />
                    </div>)
                }
                return toDecimal(row.original.panelMembershipPercent * 100)
            },
            aggregatedCell: () => null,
            enableSorting: false,
        },
        {
            id: 'panelRating',
            header: 'Score',
            accessorKey: 'panelRating',
            cell: ({ row }) => <GridCellRating rating={row.original.panelRating} type={agType} customClass="my-1" />,
            aggregatedCell: ({ row }) => row.original?.panelRating ? <GridCellRating rating={row.original.panelRating} type={agType} customClass="my-1" isAggregate={true} /> : null,
            enableSorting: false,
        },
    ];

    const handleModalClose = () => {
        dispatch(resetRepresentativenessData());
        onCancel();
        setRepsDetails([]);
        setGeographyDetails([]);
    }

    const getColumns = () => {
        if (type === AG_REPSCORE_TYPE.Baseline) {
            return baseColumns;
        } else {
            return [
                ...baseColumns,
                ...scoreColumns
            ]
        }
    }

    const getTitle = () => {
        switch (type) {
            case AG_REPSCORE_TYPE.Panel:
                return `Selection-Scorecard`
            case AG_REPSCORE_TYPE.Attendance:
                return `Participation-Scorecard`
            default:
                return `Representation-Details`
        }
    }

    const processRepScoreData = (data) => {
        const quartileKeys = {
            Q1: 'baselineAgeQ1',
            Q2: 'baselineAgeQ2',
            Q3: 'baselineAgeQ3',
        };

        const Q1 = data.find(item => item.hasOwnProperty(quartileKeys.Q1))[quartileKeys.Q1];
        const Q2 = data.find(item => item.hasOwnProperty(quartileKeys.Q2))[quartileKeys.Q2];
        const Q3 = data.find(item => item.hasOwnProperty(quartileKeys.Q3))[quartileKeys.Q3];

        const quartileLabels = {
            'First Quartile': `First Quartile ( ≤ ${Q1} )`,
            'Second Quartile': `Second Quartile ( ${Q1 + 1} - ${Q2} )`,
            'Third Quartile': `Third Quartile ( ${Q2 + 1} - ${Q3} )`,
            'Fourth Quartile': `Fourth Quartile ( ≥ ${Q3 + 1} )`,
        };

         const processed = data
            .filter(item => !(item.populationPercent === 0 && item.panelMembershipPercent === 0))
            .map(item => ({
                ...item,
                categoryValue: quartileLabels[item.categoryValue] || item.categoryValue,
            }));

        const existingCategories = new Set(processed.map(item => item.category));
        const noDataCategories = demographyCategories.filter(category => !existingCategories.has(category));

        const noDataCategory = noDataCategories.map(category => ({
            category,
            categoryValue: 'No data provided/available',
            populationPercent: null,
            panelMembershipPercent: null,
            panelRating: null,
            difference: null,
        }));

        const combined = [...processed, ...noDataCategory];

         return combined.sort((itemA, itemB) => {
            return demographyCategories.indexOf(itemA.category) - demographyCategories.indexOf(itemB.category);
        });
    };

    const handleExportCSV = () => {
        if (key !== 'demographics' || !repsDetails?.length) return;

        const headers = [
            ["Category", "Category Value", "Baseline Result", `${AG_REPSCORE_COLUMN_HEADER_PREFIX[type]} Result`, "Difference", "Score"]
        ];

        const grouped = repsDetails.reduce((acc, item) => {
            if (!acc[item.category]) acc[item.category] = [];
            acc[item.category].push(item);
            return acc;
        }, {});

        const data = [];

        Object.entries(grouped).forEach(([category, rows]) => {
            // 1. Add "Overall" row first — Score column only
            const overallRatingCode = rows[0]?.panelRating || '';
            const overallRating = overallRatingCode
                ? `Overall: ${AGGREGATE_SCORES_OTHER[overallRatingCode] || ''}`
                : '';

            data.push({
                Category: category,
                "Category Value": '',
                "Baseline Result": '',
                [`${AG_REPSCORE_COLUMN_HEADER_PREFIX[type]} Result`]: '',
                "Difference": '',
                "Score": overallRating
            });

            // 2. Add detail rows (blank Category for grouping)
            rows.forEach(item => {
                const baseline = item.populationPercent != null ? toDecimal(item.populationPercent * 100) : '';
                const panel = item.panelMembershipPercent != null ? toDecimal(item.panelMembershipPercent * 100) : '';
                const difference = (item.populationPercent != null && item.panelMembershipPercent != null)
                    ? calculateDifference(item.populationPercent, item.panelMembershipPercent)
                    : '';

                const ratingCode = difference !== '' ? getScoreTypeWithDifference(difference) : item.panelRating;
                const rating = AGGREGATE_SCORES_OTHER[ratingCode] || '';

                data.push({
                    Category: '',
                    "Category Value": item.categoryValue,
                    "Baseline Result": baseline,
                    [`${AG_REPSCORE_COLUMN_HEADER_PREFIX[type]} Result`]: panel,
                    "Difference": difference,
                    "Score": rating
                });
            });
        });

        const rowsArr = data.map(obj => Object.values(obj));

        exportData(data, rowsArr, headers, `${title}${id}-demographics`, 'C');
    };

    const handleExportPNG = async () => {
        if (!contentRef.current) return;

        const el = contentRef.current;

        // Store original states and update scroll containers
        const scrollContainers = [...el.querySelectorAll(".grid-with-scroll")];
        const originalStates = scrollContainers.map(sc => {
            const state = {
                element: sc,
                style: sc.getAttribute("style") || null,
                hadOverflowClass: sc.classList.contains("overflow-y-auto")
            };
            sc.classList.remove("overflow-y-auto");
            Object.assign(sc.style, { maxHeight: "none", height: "auto", overflow: "visible" });
            return state;
        });

        el.classList.add(styles.noScrollExport);

        try {
            const img = await snapdom.toPng(el, {
                scale: 4,
                width: el.scrollWidth,
                height: el.scrollHeight
            });

            const link = Object.assign(document.createElement("a"), {
                download: `${title}${id}-demographics.png`,
                href: img.src
            });
            link.click();
        } catch (err) {
            showAlert(`${messages.error.pngExportError} ${err}.`, ALERT_TYPE.ERROR);
        } finally {
            // Restore original states
            originalStates.forEach(({ element, style, hadOverflowClass }) => {
                style ? element.setAttribute("style", style) : element.removeAttribute("style");
                if (hadOverflowClass) element.classList.add("overflow-y-auto");
            });
            el.classList.remove(styles.noScrollExport);
        }
    };

    useEffect(() => {
        if (type) {
            setTitle(getTitle());
            setColumns(getColumns());
        }
    }, [type]);

    useEffect(() => {
        if (isAppLoaded) {
            dispatch(getAdvisoryGroupRepresentativenessByType(id, type));
        }
    }, [isAppLoaded, id]);

    useEffect(() => {
        if (repsDetailData) {
            const demographyData = repsDetailData.filter(item => demographyCategories?.includes(item.category));
            const geographyData = repsDetailData.filter(item => geographyCategories?.includes(item.category) && item.populationPercent >= 0.15);
            const ellipseOverlapData = repsDetailData.filter(item => item.category === 'Geographic Analysis' && item.categoryValue === 'Ellipse Overlap');

            if (demographyData.length === 0 || demographyData.every(item => item.panelMembershipPercent === 0)) {
                setRepsDetails([]);
            } else if (demographyData.length > 0) {
                const processedData = processRepScoreData(demographyData);
                setRepsDetails(processedData);
            }

            if (geographyData.length > 0) {
                setGeographyDetails(geographyData);
            }

            if (ellipseOverlapData.length === 0 || ellipseOverlapData.every(item => item.panelMembershipPercent === 0)) {
                setGeographySummary([]);
            } else {
                setGeographySummary([
                    ...ellipseOverlapData,
                    // enter blank row for the graphic
                    {
                        baselineRating: null,
                        category: "Geographic Analysis",
                        categoryValue: "",
                        panelMembershipPercent: "showGraphic",
                        panelRating: null,
                        populationPercent: null
                    }
                ]);
            }
        }
    }, [repsDetailData]);


    return (
        <Modal
            show={show}
            fullscreen={false}
            backdrop="static"
            onHide={handleModalClose}
            size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{title.replace('-', ' ')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="px-xl-3 py-xl-2">
                    <div className="d-flex justify-content-end mb-1">
                        {key === 'demographics' && (
                            <>
                                <Button
                                    variant="primary"
                                    className="me-2"
                                    disabled={!repsDetails?.length}
                                    onClick={handleExportCSV}
                                >
                                    Export CSV
                                </Button>
                                <Button
                                    variant="primary"
                                    className="me-2"
                                    disabled={!repsDetails?.length}
                                    onClick={handleExportPNG}
                                >
                                    Export Image
                                </Button>
                                <Button
                                    variant="primary"
                                    disabled={!repsDetails?.length}
                                    onClick={() => {
                                        handlePrint(null, () => contentRef.current);
                                    }}>
                                    Export PDF
                                </Button>
                            </>
                        )}
                    </div>
                    <div>
                        {repsDetailData === null || columns.length === 0 ? (
                            <Loader />
                        ) : (
                            <div
                                ref={contentRef}
                                className={styles.printableArea}>
                                <Tabs
                                    defaultActiveKey="demographics"
                                    activeKey={key}
                                    onSelect={(k) => setKey(k)}
                                    variant="underline"
                                    className="tabs mb-3"
                                >
                                    <Tab eventKey="demographics" title="Demographics">
                                        {repsDetails && (
                                            <DataGrid
                                                data={repsDetails}
                                                columns={columns}
                                                enableFilters={false}
                                                columnDisplay={hideColumn}
                                                groupBy={['category']}
                                                pagination={false}
                                                gridContainerClass="grid-with-scroll overflow-y-auto"
                                            />
                                        )}
                                    </Tab>
                                    <Tab eventKey="geography" title="Geography">
                                        {geographySummary && (
                                            <DataGrid
                                                data={geographySummary}
                                                columns={geoAnalysisColumns}
                                                enableFilters={false}
                                                columnDisplay={hideColumn}
                                                groupBy={['category']}
                                                pagination={false}
                                                noRecordMessage={messages.info.ellipseOverlapNoRecordMessage}
                                                gridContainerClass="grid-with-scroll overflow-y-auto"
                                            />
                                        )}
                                        {geographyDetails && (
                                            <DataGrid
                                                data={geographyDetails}
                                                columns={columns}
                                                enableFilters={false}
                                                columnDisplay={hideColumn}
                                                groupBy={['category']}
                                                pagination={false}
                                                gridContainerClass="grid-with-scroll overflow-y-auto"
                                            />
                                        )}
                                    </Tab>
                                </Tabs>
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

export default RepresentativenessDetailsModal;
